import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import {
  resolveVoiceRegionFields,
  resolveVoiceMerchant,
  VOICE_FILL_REVIEW_HINT,
} from '../utils/voiceOrderRegion';

const MISSING_LABELS = {
  receiver_name: 'اسم المستلم',
  receiver_phone: 'هاتف المستلم',
  receiver_address: 'العنوان',
  receiver_city: 'المدينة',
  receiver_region: 'المنطقة',
  description: 'الوصف',
  price: 'قيمة المنتج',
  merchant_name: 'التاجر',
};

/** Target rate for Whisper — keeps JSON payload small under nginx limits. */
const WHISPER_SAMPLE_RATE = 16000;
/** Soft cap ~45s of speech after downsample (~1.4MB WAV / ~1.9MB base64). */
const MAX_WAV_SAMPLES = WHISPER_SAMPLE_RATE * 45;

/**
 * Encode AudioBuffer as 16-bit mono PCM WAV at 16 kHz.
 * Groq Whisper accepts this reliably (browser WebM often fails).
 */
function audioBufferToWavBlob(audioBuffer) {
  const numChannels = 1;
  const sourceRate = audioBuffer.sampleRate || WHISPER_SAMPLE_RATE;
  const sourceChannels = audioBuffer.numberOfChannels;
  const sourceLength = audioBuffer.length;

  // Mix down to mono at source rate
  const mono = new Float32Array(sourceLength);
  for (let ch = 0; ch < sourceChannels; ch += 1) {
    const data = audioBuffer.getChannelData(ch);
    for (let i = 0; i < sourceLength; i += 1) {
      mono[i] += data[i] / sourceChannels;
    }
  }

  // Downsample to 16 kHz (linear interpolation)
  const ratio = sourceRate / WHISPER_SAMPLE_RATE;
  let outLength = Math.max(1, Math.floor(sourceLength / ratio));
  if (outLength > MAX_WAV_SAMPLES) outLength = MAX_WAV_SAMPLES;
  const samples = new Float32Array(outLength);
  for (let i = 0; i < outLength; i += 1) {
    const srcPos = i * ratio;
    const i0 = Math.floor(srcPos);
    const i1 = Math.min(i0 + 1, sourceLength - 1);
    const t = srcPos - i0;
    samples[i] = mono[i0] * (1 - t) + mono[i1] * t;
  }

  const sampleRate = WHISPER_SAMPLE_RATE;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = outLength * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeStr = (offset, str) => {
    for (let i = 0; i < str.length; i += 1) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < outLength; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Convert MediaRecorder blob (webm/mp4/ogg) → WAV for Whisper compatibility.
 * Falls back to original blob if decode fails.
 */
async function toWhisperFriendlyBlob(blob) {
  if (!blob || blob.size < 50) return blob;
  if ((blob.type || '').includes('wav')) return blob;

  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return blob;
    const ctx = new Ctx();
    try {
      const ab = await blob.arrayBuffer();
      const decoded = await ctx.decodeAudioData(ab.slice(0));
      if (!decoded || !decoded.length) return blob;
      return audioBufferToWavBlob(decoded);
    } finally {
      if (ctx.state !== 'closed') {
        try { await ctx.close(); } catch { /* ignore */ }
      }
    }
  } catch {
    return blob;
  }
}

/**
 * Compact voice capture control for new-shipment forms.
 * Records audio → convert to WAV → server STT + LLM → onFill(fields).
 */
export default function VoiceOrderMic({
  screen = 'merchant_new_order',
  getCurrentForm,
  onFill,
  cities = [],
  regions = [],
  merchants = [],
  enabled: enabledProp,
  ready: readyProp,
  className = '',
}) {
  const [config, setConfig] = useState(null);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');
  const [transcripts, setTranscripts] = useState([]);
  const [lastTranscript, setLastTranscript] = useState('');
  const [seconds, setSeconds] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const mimeRef = useRef('audio/webm');

  const enabled = enabledProp !== undefined
    ? !!enabledProp
    : !!(config?.enabled && config?.screens?.[screen === 'company_new_order' ? 'company_new_order' : 'merchant_new_order']);
  const ready = readyProp !== undefined ? !!readyProp : !!config?.ready;

  useEffect(() => {
    if (enabledProp !== undefined && readyProp !== undefined) return undefined;
    let cancelled = false;
    api.getVoiceConfig()
      .then((data) => {
        if (!cancelled && data && !data.error) setConfig(data);
      })
      .catch(() => {
        if (!cancelled) setConfig({ enabled: false, ready: false, screens: {} });
      });
    return () => { cancelled = true; };
  }, [enabledProp, readyProp]);

  useEffect(() => () => {
    stopTracks();
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  function stopTracks() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  const pickMime = () => {
    if (typeof MediaRecorder === 'undefined') return '';
    // Prefer containers Whisper handles well; webm is last (we convert to WAV anyway)
    const candidates = [
      'audio/mp4',
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
    ];
    for (const c of candidates) {
      if (MediaRecorder.isTypeSupported(c)) return c;
    }
    return '';
  };

  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const startRecording = useCallback(async () => {
    setError('');
    setHint('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('المتصفح لا يدعم تسجيل الصوت');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
        },
      });
      streamRef.current = stream;
      const mime = pickMime();
      // Store base type without codecs for Blob assembly
      const baseMime = (mime || 'audio/webm').split(';')[0];
      mimeRef.current = baseMime;
      const mr = mime
        ? new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 128000 })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorderRef.current = mr;
      // Single blob on stop — more reliable than timeslice chunks for containers
      mr.start();
      setRecording(true);
      setSeconds(0);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          // Auto-stop around 40s so payload stays under proxy limits
          if (s + 1 >= 40 && mediaRecorderRef.current) {
            // submit handled by user; just cap timer display
            return 40;
          }
          return s + 1;
        });
      }, 1000);
    } catch {
      setError('تعذر الوصول للميكروفون — اسمح بالإذن ثم أعد المحاولة');
    }
  }, []);

  const cancelRecording = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.stop();
      }
    } catch { /* ignore */ }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    stopTracks();
    setRecording(false);
    setSeconds(0);
  }, []);

  const submitRecording = useCallback(async () => {
    if (!mediaRecorderRef.current) return;
    setError('');
    setHint('');
    setProcessing(true);

    const mr = mediaRecorderRef.current;
    const blob = await new Promise((resolve) => {
      const finish = () => {
        const type = mimeRef.current || mr.mimeType || 'audio/webm';
        const b = new Blob(chunksRef.current, { type: type.split(';')[0] });
        resolve(b);
      };
      mr.onstop = finish;
      try {
        if (mr.state !== 'inactive') {
          // Flush any remaining data then stop
          try { if (typeof mr.requestData === 'function') mr.requestData(); } catch { /* ignore */ }
          mr.stop();
        } else {
          finish();
        }
      } catch {
        finish();
      }
    });

    if (timerRef.current) clearInterval(timerRef.current);
    stopTracks();
    setRecording(false);
    mediaRecorderRef.current = null;

    if (!blob || blob.size < 200) {
      setProcessing(false);
      setError('التسجيل قصير جداً — سجّل جملة أوضح (ثانيتان على الأقل)');
      return;
    }

    try {
      // Convert to WAV so Groq Whisper always gets a valid media file
      const friendly = await toWhisperFriendlyBlob(blob);
      const audioBase64 = await blobToBase64(friendly);
      const currentForm = typeof getCurrentForm === 'function' ? getCurrentForm() : {};
      const res = await api.voiceOrderFill({
        audio_base64: audioBase64,
        mime_type: friendly.type || 'audio/wav',
        previous_transcripts: transcripts,
        current_form: currentForm,
        screen,
      });

      if (res.error) throw new Error(res.error);

      const nextTranscripts = Array.isArray(res.transcripts)
        ? res.transcripts
        : [...transcripts, res.transcript].filter(Boolean);
      setTranscripts(nextTranscripts);
      setLastTranscript(res.transcript || '');

      const fields = res.fields || {};
      let patch = { ...fields };
      if (!patch.receiver_city_id && patch.receiver_city && cities.length) {
        const q = String(patch.receiver_city).trim().toLowerCase();
        const match = cities.find((c) => {
          const ar = String(c.name_ar || '').toLowerCase();
          const en = String(c.name || '').toLowerCase();
          return ar === q || en === q || ar.includes(q) || en.includes(q) || q.includes(ar);
        });
        if (match) {
          patch.receiver_city_id = match.id;
          patch.receiver_city = match.name_ar || match.name || patch.receiver_city;
        }
      }

      // Prefer regions for the resolved city; fall back to prop list
      let regionList = regions;
      if (patch.receiver_city_id) {
        try {
          const remote = await api.getRegions(patch.receiver_city_id);
          if (Array.isArray(remote) && remote.length) regionList = remote;
        } catch { /* use prop list */ }
      }
      const regionResolved = resolveVoiceRegionFields(patch, regionList);
      patch.receiver_region_id = regionResolved.receiver_region_id;
      patch.receiver_custom_region = regionResolved.receiver_custom_region;
      if (regionResolved.receiver_region) patch.receiver_region = regionResolved.receiver_region;
      patch.usedCustomRegion = regionResolved.usedCustomRegion;

      let merchantResolved = null;
      if (screen === 'company_new_order' && merchants.length) {
        merchantResolved = resolveVoiceMerchant(merchants, {
          merchant_id: patch.merchant_id || currentForm.merchant_id,
          merchant_name: patch.merchant_name,
        });
        if (merchantResolved.matched) {
          patch.merchant_id = merchantResolved.merchant_id;
          patch.merchant_name = merchantResolved.merchant_name;
        } else if (patch.merchant_name) {
          patch.merchantMatchFailed = true;
        }
      }

      if (typeof onFill === 'function') onFill(patch, res);

      const missing = Array.isArray(res.missing_fields) ? res.missing_fields : [];
      const parts = [];
      if (merchantResolved?.matched) {
        parts.push(`تم اختيار التاجر: ${merchantResolved.merchant_name}`);
      } else if (patch.merchantMatchFailed && patch.merchant_name) {
        parts.push(`لم يُعثر على التاجر «${patch.merchant_name}» في القائمة — اختره يدوياً`);
      }
      if (regionResolved.usedCustomRegion && regionResolved.receiver_custom_region) {
        parts.push(
          `المنطقة «${regionResolved.receiver_custom_region}» غير مدرجة في القائمة — تم اختيار «آخر» وكتابة الاسم`,
        );
      }
      if (missing.length) {
        const labels = missing.map((k) => MISSING_LABELS[k] || k).join('، ');
        parts.push(res.notes_ar
          ? `${res.notes_ar} — ناقص: ${labels}`
          : `تم التعبئة جزئياً. ناقص: ${labels}. يمكنك التسجيل مجدداً لإكمال البيانات`);
      } else if (res.notes_ar) {
        parts.push(res.notes_ar);
      } else {
        parts.push('تم تعبئة النموذج من الصوت');
      }
      parts.push(VOICE_FILL_REVIEW_HINT);
      setHint(parts.join(' — '));
    } catch (err) {
      setError(err.message || 'فشل معالجة الصوت');
    } finally {
      setProcessing(false);
      setSeconds(0);
    }
  }, [getCurrentForm, onFill, screen, transcripts, cities, regions, merchants]);

  if (!enabled) return null;

  return (
    <div className={`vi-voice-mic ${className}`.trim()}>
      <div className="vi-voice-mic__row">
        <div className="vi-voice-mic__label">
          <span className="vi-voice-mic__title">تعبئة بالصوت</span>
          {!ready && (
            <span className="vi-voice-mic__badge vi-voice-mic__badge--warn">غير جاهز (مفتاح API)</span>
          )}
          {ready && transcripts.length > 0 && (
            <span className="vi-voice-mic__badge">
              {transcripts.length} مقطع
            </span>
          )}
        </div>

        <div className="vi-voice-mic__controls">
          {!recording && !processing && (
            <button
              type="button"
              className="vi-voice-mic__btn vi-voice-mic__btn--idle"
              onClick={startRecording}
              disabled={!ready}
              title="تسجيل صوت لتعبئة النموذج"
              aria-label="بدء التسجيل الصوتي"
            >
              <MicIcon />
            </button>
          )}
          {recording && (
            <>
              <span className="vi-voice-mic__timer" dir="ltr">
                {String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}
              </span>
              <button
                type="button"
                className="vi-voice-mic__btn vi-voice-mic__btn--rec"
                onClick={submitRecording}
                title="إيقاف وإرسال"
                aria-label="إيقاف وإرسال التسجيل"
              >
                <span className="vi-voice-mic__pulse" />
                <MicIcon />
              </button>
              <button
                type="button"
                className="vi-voice-mic__cancel"
                onClick={cancelRecording}
                aria-label="إلغاء التسجيل"
              >
                إلغاء
              </button>
            </>
          )}
          {processing && (
            <div className="vi-voice-mic__processing" aria-live="polite">
              <span className="vi-voice-mic__spinner" />
              جاري التحويل…
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="vi-voice-mic__msg vi-voice-mic__msg--error" role="alert">
          {error}
        </div>
      )}
      {!error && hint && (
        <div className="vi-voice-mic__msg vi-voice-mic__msg--hint" role="status">
          {hint}
        </div>
      )}
      {lastTranscript && !error && (
        <div className="vi-voice-mic__transcript" title="آخر نص مستخرج">
          «{lastTranscript}»
        </div>
      )}
      {recording && (
        <p className="vi-voice-mic__help">
          اذكر اسم المستلم، الهاتف، المدينة، العنوان، وقيمة المنتج — ثم اضغط الميكروفون للإرسال
        </p>
      )}
    </div>
  );
}

function MicIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Z"
        fill="currentColor"
      />
      <path
        d="M19 11a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.93V21a1 1 0 1 0 2 0v-3.07A7 7 0 0 0 19 11Z"
        fill="currentColor"
      />
    </svg>
  );
}
